import { useState, useMemo } from 'react';
import { safeFormat } from '@/lib/safeDate';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Package, Calendar, Loader2, Merge } from 'lucide-react';
import { DealForMerge } from '@/hooks/useMergeDeals';
import { formatCurrency } from '@/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface MergeDealsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactName: string;
  deals: DealForMerge[];
  onConfirm: (dealDestinoId: string, dealOrigemIds: string[]) => Promise<void>;
  merging?: boolean;
}

export function MergeDealsDialog({
  open,
  onOpenChange,
  contactName,
  deals,
  onConfirm,
  merging = false
}: MergeDealsDialogProps) {
  const [selectedDestino, setSelectedDestino] = useState<string>('');
  const [selectedOrigens, setSelectedOrigens] = useState<Set<string>>(new Set());

  // Reset when dialog opens
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setSelectedDestino('');
      setSelectedOrigens(new Set());
    }
    onOpenChange(isOpen);
  };

  // Toggle origem selection
  const toggleOrigem = (dealId: string) => {
    const newSet = new Set(selectedOrigens);
    if (newSet.has(dealId)) {
      newSet.delete(dealId);
    } else {
      newSet.add(dealId);
    }
    setSelectedOrigens(newSet);
  };

  // Calculate preview
  const preview = useMemo(() => {
    if (!selectedDestino || selectedOrigens.size === 0) return null;

    const destinoDeal = deals.find(d => d.id === selectedDestino);
    const origensDeals = deals.filter(d => selectedOrigens.has(d.id));

    if (!destinoDeal) return null;

    const paymentsToMove = origensDeals.reduce((sum, d) => sum + d.payments_count, 0);
    const totalPayments = destinoDeal.payments_count + paymentsToMove;
    const newTotal = deals
      .filter(d => d.id === selectedDestino || selectedOrigens.has(d.id))
      .reduce((sum, d) => sum + d.total_amount, 0);

    return {
      destinoDeal,
      origensDeals,
      paymentsToMove,
      totalPayments,
      newTotal,
      dealsToDelete: origensDeals.map(d => d.name)
    };
  }, [selectedDestino, selectedOrigens, deals]);

  const handleConfirm = async () => {
    if (!selectedDestino || selectedOrigens.size === 0) return;
    await onConfirm(selectedDestino, Array.from(selectedOrigens));
    handleOpenChange(false);
  };

  const canConfirm = selectedDestino && selectedOrigens.size > 0 && !merging;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Merge className="h-5 w-5" />
            Mesclar Deals - {contactName}
          </DialogTitle>
          <DialogDescription>
            Selecione o deal destino e os deals que serão mesclados nele.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Step 1: Select destination */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">
              1. Selecione o deal DESTINO (será mantido):
            </Label>
            <RadioGroup value={selectedDestino} onValueChange={(val) => {
              setSelectedDestino(val);
              // Remove from origens if it was selected
              const newOrigens = new Set(selectedOrigens);
              newOrigens.delete(val);
              setSelectedOrigens(newOrigens);
            }}>
              {deals.map(deal => (
                <div
                  key={deal.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                    selectedDestino === deal.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-muted-foreground/50'
                  }`}
                >
                  <RadioGroupItem value={deal.id} id={`dest-${deal.id}`} className="mt-1" />
                  <Label htmlFor={`dest-${deal.id}`} className="flex-1 cursor-pointer">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{deal.name}</span>
                      <Badge variant="outline">
                        {formatCurrency(deal.total_amount)}
                      </Badge>
                      {deal.source === 'quickbooks' && (
                        <Badge variant="secondary" className="text-xs">
                          QuickBooks
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                      <span className="flex items-center gap-1">
                        <Package className="h-3 w-3" />
                        {deal.payments_count} payments
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {safeFormat(new Date(deal.created_at), 'dd/MM/yyyy', { locale: ptBR })}
                      </span>
                    </div>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Step 2: Select origins */}
          {selectedDestino && (
            <div className="space-y-3">
              <Label className="text-base font-semibold">
                2. Selecione deals para MESCLAR no destino:
              </Label>
              <div className="space-y-2">
                {deals
                  .filter(d => d.id !== selectedDestino)
                  .map(deal => (
                    <div
                      key={deal.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                        selectedOrigens.has(deal.id)
                          ? 'border-destructive/50 bg-destructive/5'
                          : 'border-border hover:border-muted-foreground/50'
                      }`}
                    >
                      <Checkbox
                        id={`orig-${deal.id}`}
                        checked={selectedOrigens.has(deal.id)}
                        onCheckedChange={() => toggleOrigem(deal.id)}
                        className="mt-1"
                      />
                      <Label htmlFor={`orig-${deal.id}`} className="flex-1 cursor-pointer">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{deal.name}</span>
                          <Badge variant="outline">
                            {formatCurrency(deal.total_amount)}
                          </Badge>
                          {deal.source === 'quickbooks' && (
                            <Badge variant="secondary" className="text-xs">
                              QuickBooks
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <Package className="h-3 w-3" />
                            {deal.payments_count} payments
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {safeFormat(new Date(deal.created_at), 'dd/MM/yyyy', { locale: ptBR })}
                          </span>
                        </div>
                      </Label>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Preview */}
          {preview && (
            <div className="rounded-lg border border-warning bg-warning/10 p-4 space-y-2">
              <div className="flex items-center gap-2 font-semibold text-warning-foreground">
                <AlertTriangle className="h-5 w-5" />
                O que vai acontecer:
              </div>
              <ul className="text-sm space-y-1 ml-7 list-disc">
                <li>
                  <strong>{preview.paymentsToMove}</strong> payments serão movidos para "{preview.destinoDeal.name}"
                </li>
                <li>
                  <strong>{preview.origensDeals.length}</strong> deal(s) serão deletados:{' '}
                  {preview.dealsToDelete.map((n, i) => (
                    <span key={i}>
                      "{n}"{i < preview.dealsToDelete.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </li>
                <li>
                  Total do deal destino: <strong>{formatCurrency(preview.newTotal)}</strong>{' '}
                  ({preview.totalPayments} payments)
                </li>
              </ul>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={merging}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="gap-2"
          >
            {merging ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Mesclando...
              </>
            ) : (
              <>
                <Merge className="h-4 w-4" />
                Confirmar Merge
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
